// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge, Modal } from '@loa/ui-components';
import type { QuoteTerm } from '../api/quoteTermApi';
import { RateCardOptionCard } from './quotes/RateCardOptionCard';

type Props = Readonly<{
  open: boolean;
  card: QuoteTerm;
  onClose: () => void;
}>;

/**
 * Read-only viewer for a provider rate card — shows every option (A/B/C/D…)
 * with its full parameter set, laid out as quote-style cards. Opened from the
 * "View" action on the Provider Settings → Terms & Quotes table, for both the
 * brokerage's own cards and inherited platform defaults. See quotes-engine-prd.md §7.2.
 */
export function QuoteTermOptionsModal({ open, card, onClose }: Props) {
  const isPlatform = card.scope === 'PLATFORM';
  const options = card.options ?? [];

  return (
    <Modal open={open} onClose={onClose} size="xlarge">
      <Modal.Header icon="document" title={`${card.productName ?? 'Rate card'} — Options`} onClose={onClose} />
      <Modal.Content>
        <div className="flex items-center gap-2 mb-4" data-testid="rate-card-meta">
          {card.providerName && <Badge color="neutral" variant="outlined">{card.providerName}</Badge>}
          {card.category && <Badge color="neutral" variant="outlined">{card.category}</Badge>}
          <Badge color="neutral" variant="outlined">{card.currencyCode}</Badge>
          <Badge color={isPlatform ? 'blue' : 'purple'} variant={isPlatform ? 'outlined' : 'filled'}>
            {isPlatform ? 'Platform' : 'Yours'}
          </Badge>
        </div>

        {options.length === 0 ? (
          <p className="text-secondary text-sm">This rate card has no options.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rate-card-options-grid">
            {options.map((option) => (
              <RateCardOptionCard key={option.id ?? option.optionCode} option={option} />
            ))}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions secondaryAction={{ label: 'Close', onClick: onClose }} />
    </Modal>
  );
}
